import { onlyNumbers } from '@brazilian-utils/brazilian-utils';
import isValidCep from '@brazilian-utils/is-valid-cep';
import TextField from '@components/atoms/TextField';
import { AddressFormResult, QuoteFinishForm } from '@lib/data/typesQuestions';
import { reportError } from '@lib/services/sentry';
import cepPromise, { CEP } from 'cep-promise';
import FormField from 'components/atoms/FormField';
import GridBox from 'components/atoms/GridBox';
import OnboardingButtonBar from 'components/molecules/button-bar/OnboardingButtonBar';
import { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

const Form = styled.form``;

interface Props {
  onSubmit: (data: AddressFormResult) => any;
  loading: boolean;
  item: QuoteFinishForm;
}

const AddressForm: FC<Props> = ({ onSubmit, loading, item }) => {
  const [searchingCep, setSearchingCep] = useState(false);
  const [cepData, setCepData] = useState<CEP | null>(null);

  const {
    handleSubmit,
    errors,
    register,
    setError,
    watch,
    getValues,
    setValue,
  } = useForm<{
    number: string;
    complement: string;
    cep: string;
    state: string;
    city: string;
    street: string;
    neighborhood: string;
  }>();

  const searchCep = useCallback(
    async (cep: string) => {
      setError('cep', {});
      setSearchingCep(true);
      try {
        const data = await cepPromise(cep);

        setValue('street', data.street);
        setValue('city', data.city);
        setValue('neighborhood', data.neighborhood);
        setValue('state', data.state);
        setValue('street', data.street);
        setCepData(data);
      } catch (error: any) {
        reportError(error);
        if (error.type === 'service_error') {
          setError('cep', {
            message: 'CEP não encontrado, verifique e tente novamente',
            shouldFocus: true,
          });
        }
      }
      setSearchingCep(false);
    },
    [setValue, setError],
  );

  const watchedCep = watch().cep;

  useEffect(() => {
    const { cep } = getValues();
    if (
      isValidCep(cep) &&
      (onlyNumbers(cep) !==
        onlyNumbers(item?.vehicle?.overnightAddress?.cep as string) ||
        onlyNumbers(cep) !== onlyNumbers(item.address?.cep as string))
    ) {
      searchCep(cep);
    }
  }, [watchedCep, getValues, searchCep, item]);

  return (
    <Form
      onSubmit={handleSubmit(async (data) => {
        if (
          item?.vehicle?.overnightAddress &&
          onlyNumbers(data.cep) ===
            onlyNumbers(item?.vehicle?.overnightAddress?.cep as string)
        ) {
          onSubmit({
            address: {
              ...data,
              cep: item.vehicle.overnightAddress.cep,
              city: item.vehicle.overnightAddress.city || data.city,
              street: item.vehicle.overnightAddress.street || data.street,
              neighborhood:
                item.vehicle.overnightAddress.neighborhood || data.neighborhood,
              state: item.vehicle.overnightAddress.state || data.state,
            },
          });
        }

        if (
          item.address?.cep &&
          onlyNumbers(data.cep) === onlyNumbers(item.address?.cep as string)
        ) {
          onSubmit({
            address: {
              ...data,
            },
          });
        }

        if (cepData) {
          onSubmit({
            address: {
              ...data,
              cep: cepData.cep,
              city: cepData.city || data.city,
              street: cepData.street || data.street,
              neighborhood: cepData.neighborhood || data.neighborhood,
              state: cepData.state || data.state,
            },
          });
        }
      })}
    >
      <GridBox>
        <GridBox width={[1, 0.55]}>
          <FormField label="Endereço" style={{ width: '100%' }}>
            <TextField
              name="street"
              defaultValue={
                item.address?.street ||
                item?.vehicle?.overnightAddress?.street ||
                ''
              }
              errorMessage={errors?.street?.message}
              inputRef={register({
                required: 'Obrigatório',
              })}
              disabled={searchingCep}
              autoComplete="off"
            />
          </FormField>
        </GridBox>
        <GridBox width={[0.35]}>
          <FormField label="Número" style={{ width: '100%' }}>
            <TextField
              name="number"
              placeholder="Número"
              defaultValue={item.address?.number || ''}
              errorMessage={errors?.number?.message}
              inputRef={register({
                required: 'Precisamos de um número',
              })}
              autoComplete="off"
            />
          </FormField>
        </GridBox>

        <GridBox width={[0.47]}>
          <FormField label="Complemento" style={{ width: '100%' }}>
            <TextField
              name="complement"
              placeholder="Complemento"
              defaultValue={item.address?.complement || ''}
              errorMessage={errors?.complement?.message}
              inputRef={register({})}
              autoComplete="off"
            />
          </FormField>
        </GridBox>
        <GridBox width={[0.47]}>
          <FormField label="CEP 🇧🇷" style={{ width: '100%' }}>
            <TextField
              placeholder="CEP"
              inputRef={register({
                required: 'Precisamos de um CEP',
                validate: {
                  valid: (s) => isValidCep(s) || 'Precisamos de um CEP válido',
                },
              })}
              autoComplete="shipping postal-code"
              type="tel"
              errorMessage={errors.cep?.message}
              disabled={searchingCep}
              name="cep"
              mask={[/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/]}
              defaultValue={
                item.address?.cep || item?.vehicle?.overnightAddress?.cep || ''
              }
            />
          </FormField>
        </GridBox>

        <GridBox width={[0.47]}>
          <FormField label="Bairro" style={{ width: '100%' }}>
            <TextField
              name="neighborhood"
              defaultValue={
                item.address?.neighborhood ||
                item?.vehicle?.overnightAddress?.neighborhood ||
                ''
              }
              errorMessage={errors?.neighborhood?.message}
              inputRef={register({
                required: 'Obrigatório',
              })}
              autoComplete="off"
              disabled={searchingCep}
            />
          </FormField>
        </GridBox>
      </GridBox>

      <OnboardingButtonBar loading={loading || searchingCep} />
    </Form>
  );
};

export default AddressForm;
